import RouteLayout from "@/components/RouteLayout";
import SeoHead from "@/components/SeoHead";
import ArticleShell, { buildFaqJsonLd } from "@/components/ArticleShell";
import CoastalNewsSection from "@/components/CoastalNewsSection";

const faqs = [
  {
    q: "How do I check if a San Diego beach is safe to swim today?",
    a: "Check the San Diego County Beach & Bay Water Quality status page before you swim. The county tests for enterococcus and total coliform bacteria daily in summer, and posts warnings (yellow) or closures (red) at affected beaches. After any measurable rain, treat the ocean as contaminated for 72 hours — this is the standard advisory window across San Diego County.",
  },
  {
    q: "Why is Imperial Beach so often closed?",
    a: "Imperial Beach and the South Bay shoreline sit immediately downcurrent of the Tijuana River, which carries cross-border sewage and stormwater into the Pacific. During wet months and after spills the plume drifts north and triggers extended closures. Long-term federal infrastructure projects are underway, but until they're complete, frequent advisories in Imperial Beach, Silver Strand, and Coronado's south end are the norm.",
  },
  {
    q: "What's a San Diego beach advisory versus a closure?",
    a: "An advisory (yellow sign) means bacteria levels exceed safe thresholds and contact with the water is not recommended. A closure (red sign) means the water is confirmed contaminated — usually by a sewage spill — and entry is prohibited. Both are issued by the San Diego County Department of Environmental Health and posted at lifeguard towers and beach entrances.",
  },
  {
    q: "When are San Diego king tides in 2026?",
    a: "King tides — the highest tides of the year — hit San Diego twice annually around the December–January and June–July new and full moons. They flood low-lying boardwalks (Mission Beach, Imperial Beach), narrow the sand at Coronado, and create dramatic spray shows at Sunset Cliffs. NOAA's tide predictions are the authoritative schedule; the Coastal Commission runs an annual King Tides photo project around the December event.",
  },
  {
    q: "Where can I report water pollution or a sewage spill in San Diego?",
    a: "Call San Diego County Department of Environmental Health at 858-694-3886 for water-quality complaints. Visible spills go to the U.S. Coast Guard National Response Center at 800-424-8802. Plastic and trash issues can be reported to Surfrider Foundation San Diego, which also runs a Blue Water Task Force lab testing samples weekly.",
  },
  {
    q: "Is the La Jolla kelp forest recovering?",
    a: "Yes — slowly. Decades of urchin overgrazing thinned the giant-kelp canopy off La Jolla, but restoration work led by The Bay Foundation and local divers has helped sections rebound. Volunteer dive days and shore-based cleanups are organized through Surfrider San Diego and San Diego Coastkeeper.",
  },
];

const News = () => (
  <RouteLayout>
    <SeoHead
      title="San Diego Beach Water Quality & Closures"
      description="Live San Diego beach water quality, closures, and coastal advisories — plus plain-English explainers on Tijuana sewage, king tides, and kelp restoration."
      path="/news"
      jsonLd={buildFaqJsonLd(faqs)}
      noindex
    />
    <ArticleShell
      page="news"
      breadcrumb={[{ label: "Water Quality & Advisories" }]}
      eyebrow="Coastal News"
      title="San Diego Water Quality &"
      titleAccent="Beach Advisories"
      intro={
        <>
          <p>
            Before you swim, surf, or wade in San Diego, it's worth a
            two-minute check on <strong>San Diego water quality</strong> and
            any active <strong>beach closures</strong> or
            advisories. The county tests dozens of shoreline sites for
            bacteria, posts warnings when levels exceed safe thresholds, and
            shuts beaches entirely when sewage is confirmed in the surf zone.
          </p>
          <p>
            This page is a plain-English guide to the closures and
            advisories that actually affect how you spend time on the coast —
            how to read the county's beach status, why Imperial Beach is
            closed so often, what triggers a 72-hour rain advisory, when
            king tides flood the boardwalks, and where the kelp restoration
            work is happening off La Jolla. The live county and local news
            feed sits at the bottom of the page so you can scan today's
            headlines after you've got the context.
          </p>
        </>
      }
      section={
        <>
          <section id="water-quality" className="bg-background px-6 py-12 scroll-mt-24">
            <div className="mx-auto max-w-3xl">
              <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">Water quality</p>
              <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">How San Diego water quality is tested</h2>
              <div className="font-body mt-4 space-y-4 text-base leading-relaxed text-foreground/85">
                <p>
                  The San Diego County Department of Environmental Health
                  samples coastal water at more than 70 shoreline sites
                  during the summer season and weekly year-round in the
                  South Bay. Lab tests measure <em>enterococcus</em> and
                  total coliform — bacteria that indicate fecal
                  contamination from sewage, stormwater, or wildlife. When
                  a sample exceeds state thresholds, the county issues a
                  warning the same day. When a known spill enters the
                  surf zone, beaches are closed outright pending two
                  consecutive clean samples.
                </p>
                <p>
                  The most common cause of an advisory in San Diego isn't
                  a dramatic spill — it's rain. Even a tenth of an inch
                  flushes streets, storm drains, and canyons full of
                  bacteria into the ocean. The county's standing rule:
                  stay out of the water for 72 hours after any measurable
                  rainfall, countywide.
                </p>
              </div>
            </div>
          </section>

          <section id="beach-closures" className="bg-muted/30 px-6 py-12 scroll-mt-24">
            <div className="mx-auto max-w-3xl">
              <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">Beach closures</p>
              <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">What triggers a San Diego beach closure</h2>
              <div className="font-body mt-4 space-y-4 text-base leading-relaxed text-foreground/85">
                <p>
                  Most closures fall into three buckets: confirmed sewage
                  contamination (a spill or cross-border flow), bacterial
                  exceedance following rain, or a public-safety hazard
                  (shark sighting, water rescue, hazardous debris). The
                  first two come from the county Department of
                  Environmental Health; the third comes from city
                  lifeguards and can lift within hours.
                </p>
                <p>
                  Areas that close most frequently: Imperial Beach,
                  Silver Strand State Beach, the south end of Coronado,
                  and Border Field State Park during wet months. Areas
                  that close almost never: La Jolla Shores, Del Mar, the
                  North County beaches from Cardiff to Oceanside.
                </p>
              </div>
            </div>
          </section>

          <section id="advisories" className="bg-background px-6 py-12 scroll-mt-24">
            <div className="mx-auto max-w-3xl">
              <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">Advisories</p>
              <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">Reading a San Diego beach advisory sign</h2>
              <div className="font-body mt-4 space-y-4 text-base leading-relaxed text-foreground/85">
                <p>
                  Yellow signs mean an advisory: bacteria levels exceed
                  safe thresholds and water contact is not recommended.
                  Red signs mean a closure: the water is confirmed
                  contaminated and entry is prohibited. White postings
                  are general information — usually a rain advisory
                  across all county beaches. If you see any of these at
                  a lifeguard tower or beach entrance, take them
                  seriously: they're posted only after lab confirmation
                  or a documented spill.
                </p>
              </div>
            </div>
          </section>

          <section id="tijuana-sewage" className="bg-muted/30 px-6 py-12 scroll-mt-24">
            <div className="mx-auto max-w-3xl">
              <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">Cross-border pollution</p>
              <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">The Tijuana sewage problem, explained</h2>
              <div className="font-body mt-4 space-y-4 text-base leading-relaxed text-foreground/85">
                <p>
                  The Tijuana River drains a large urban watershed in
                  Mexico that lacks the wastewater capacity to handle
                  storm flows and aging-infrastructure spills. The
                  result: millions of gallons of untreated sewage flow
                  north across the border each year, enter the ocean
                  near Imperial Beach, and drift up the coast on
                  prevailing currents. It's the single largest reason
                  Imperial Beach holds the worst water-quality record
                  in California.
                </p>
                <p>
                  Federal funding for upgrades to the South Bay
                  International Wastewater Treatment Plant is moving
                  forward, but completion is years out. Until then,
                  Imperial Beach and the south end of Coronado will
                  see frequent closures, especially in winter.
                </p>
              </div>
            </div>
          </section>

          <section id="king-tides" className="bg-background px-6 py-12 scroll-mt-24">
            <div className="mx-auto max-w-3xl">
              <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">King tides</p>
              <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">When San Diego king tides hit</h2>
              <div className="font-body mt-4 space-y-4 text-base leading-relaxed text-foreground/85">
                <p>
                  King tides — the year's highest astronomical tides —
                  arrive in two windows: December through early January,
                  and June through July, around the new and full moons.
                  They typically push water 7 feet or more above the
                  lower lows, flooding portions of the Mission Beach
                  boardwalk, narrowing the sand at Coronado and
                  Imperial Beach, and turning Sunset Cliffs into a wave-
                  and-spume show. They're also a preview of average sea
                  level a few decades out.
                </p>
                <p>
                  See today's tide on the <a href="/tides" className="text-primary underline">live tide chart</a>{" "}
                  and watch for posted warnings in low-lying areas during
                  king-tide weeks.
                </p>
              </div>
            </div>
          </section>

          <section id="kelp-restoration" className="bg-muted/30 px-6 py-12 scroll-mt-24">
            <div className="mx-auto max-w-3xl">
              <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">Kelp restoration</p>
              <h2 className="font-display mt-3 text-3xl font-semibold text-foreground sm:text-4xl">La Jolla kelp restoration</h2>
              <div className="font-body mt-4 space-y-4 text-base leading-relaxed text-foreground/85">
                <p>
                  The giant-kelp forest off La Jolla is one of San
                  Diego's signature underwater ecosystems — and it has
                  been thinning for decades, mostly because purple
                  urchins (no longer controlled by sea otters along this
                  stretch of coast) overgraze young kelp. Restoration
                  divers from The Bay Foundation, Sundiver, and local
                  volunteer dive teams cull urchins in targeted plots,
                  letting kelp recolonize. Sections of the cove are
                  noticeably greener than five years ago.
                </p>
                <p>
                  Surfrider San Diego and San Diego Coastkeeper organize
                  monthly shoreline cleanups that anyone can join — the
                  easiest way to plug in if you don't dive.
                </p>
              </div>
            </div>
          </section>

          <CoastalNewsSection />
        </>
      }
      bestFor={[
        { label: "Best for daily swim-safety checks", pickName: "County Beach & Bay Status", description: "The authoritative source for today's San Diego water quality, advisories, and closures.", to: "/news#water-quality", related: { label: "Beach guide", to: "/beaches" } },
        { label: "Best for understanding closures", pickName: "Tijuana sewage explainer", description: "Why Imperial Beach and the South Bay close so often — and what's being done about it.", to: "/news#tijuana-sewage", related: { label: "Imperial Beach", to: "/beaches#imperial-beach" } },
        { label: "Best for rain-day planning", pickName: "72-hour rain advisory rule", description: "After any measurable rain, the county recommends staying out of the water for 72 hours.", to: "/news#water-quality" },
        { label: "Best for king-tide planning", pickName: "Dec–Jan & Jun–Jul windows", description: "Highest tides of the year — flooded boardwalks, narrowed beaches, dramatic spray at Sunset Cliffs.", to: "/news#king-tides", related: { label: "Live tide chart", to: "/tides#tide-chart" } },
        { label: "Best for surf advisories", pickName: "Lifeguard channels", description: "City lifeguard social accounts post real-time rip-current and visibility warnings before they hit news.", to: "/news#advisories", related: { label: "Live surf report", to: "/surf#conditions" } },
        { label: "Best for conservation", pickName: "La Jolla kelp restoration", description: "Volunteer cleanups, dive days, and policy work led by Surfrider and Coastkeeper.", to: "/news#kelp-restoration" },
      ]}
      tips={[
        { title: "Check the county before any south-county swim", body: "San Diego County's Beach & Bay Water Quality page is updated daily in summer. It's the only source that combines lab results, spill reports, and lifeguard postings in one place." },
        { title: "Follow the 72-hour rain rule", body: "After any measurable rain, treat the ocean as contaminated for 72 hours — countywide, not just South Bay. Storm drains move bacteria fast." },
        { title: "Watch for the colored signs at the beach", body: "Yellow = advisory (avoid contact). Red = closure (no entry). White = general/rain advisory. All are posted only after lab confirmation or a documented spill." },
        { title: "Use lifeguard social accounts for real-time alerts", body: "City lifeguard Twitter/X and Instagram accounts post rip current warnings, water rescues, and visibility alerts before they reach news outlets." },
        { title: "Report spills to the right hotline", body: "Water-quality complaints: SD County Environmental Health 858-694-3886. Visible spills: U.S. Coast Guard 800-424-8802. Trash and plastic: Surfrider San Diego." },
        { title: "Plan king-tide weeks around the schedule", body: "Highest tides cluster in Dec–Jan and Jun–Jul around new and full moons. Avoid parking in low-lying boardwalk lots if a king tide is forecast." },
      ]}
      faqs={faqs}
    />
  </RouteLayout>
);

export default News;
